import { ApiResponse, OrganizationDefaultsResponse } from '@/modules/common';
import { mapOrganizationDefaultsFromResponse } from '@/modules/common/mappers/organization-defaults.mapper';
import { mapOrganizationSummaryFromResponse } from '@/modules/common/mappers/organization-summary.mapper';
import { mapRateSourceToDTO } from '@/modules/common/mappers/rate-source.mapper';
import {
  OrganizationRateSourceDTO,
  RateSourceDTO,
} from '@/modules/common/types/dto/rate-source.dto';
import { OrganizationSummaryResponse } from '@/modules/common/types/dto/response/organization-summary.response';
import { Organization, OrganizationUpdateRequest } from '@/modules/common/types/organization';
import { OrganizationDefaults } from '@/modules/common/types/organization-defaults';
import { OrganizationSummary } from '@/modules/common/types/organization-summary';
import { RateSource } from '@/modules/common/types/rate-source';
import { PatchOrganizationExternalRatesRequest } from '@/modules/settings/types/request/patch-organization-external-rates-request';
import { axiosInstance } from '@/utils/axios';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { ApiErrorCode, withApiErrorHandler } from './errors';

/**
 * Fetches organization defaults including currency and rate source settings.
 * Optionally accepts a revision date to get historical defaults.
 * @returns Domain model OrganizationDefaults (mapped from API response)
 */
export const getOrganizationDefaults = async (
  revisionDate?: Date
): Promise<OrganizationDefaults> => {
  const endpoint = '/api/organizations/me/defaults';
  const params = revisionDate
    ? { revisionDate: normalizeToStartOfDayUTC(revisionDate).toISOString() }
    : undefined;

  const response = await withApiErrorHandler<OrganizationDefaultsResponse>(
    endpoint,
    ApiErrorCode.ORGANIZATION_DEFAULTS_LOAD_FAILED,
    () => axiosInstance.get(endpoint, { params })
  );

  return mapOrganizationDefaultsFromResponse(response);
};

/**
 * Updates organization external rates configuration including default currency,
 * rate source, and global fallback rate.
 * @param params - External rates configuration parameters
 * @returns Updated organization defaults
 */
export const patchOrganizationExternalRates = async ({
  defaultCurrencyCode,
  defaultProjectRateSource,
  defaultRate,
}: {
  defaultCurrencyCode: string;
  defaultProjectRateSource: RateSource;
  defaultRate: number;
}): Promise<void> => {
  const endpoint = '/api/organizations/me/external-rates';

  // Map domain RateSource to DTO and validate it's in the allowed subset for organization settings.
  // The UI only allows GlobalDefaultRate (→ ORGANIZATION) and UserExternalRate (→ USER),
  // but we guard here in case future callers pass an unsupported value.
  const mappedDTO = mapRateSourceToDTO(defaultProjectRateSource);
  const ALLOWED_ORG_RATE_SOURCES: readonly RateSourceDTO[] = [
    RateSourceDTO.ORGANIZATION,
    RateSourceDTO.USER,
    RateSourceDTO.SENIORITY,
    RateSourceDTO.ROLE,
  ] as const;

  if (!ALLOWED_ORG_RATE_SOURCES.includes(mappedDTO)) {
    throw new Error(`Invalid rate source for organization settings: ${mappedDTO}`);
  }
  const rateSourceDTO = mappedDTO as OrganizationRateSourceDTO;

  const requestBody: PatchOrganizationExternalRatesRequest = {
    defaultCurrencyCode,
    defaultProjectRateSource: rateSourceDTO,
    defaultRate,
  };

  const response = await axiosInstance.patch<ApiResponse<OrganizationDefaultsResponse>>(
    endpoint,
    requestBody
  );

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  // Backend doesn't return data, just success response
  // Data will be refetched via query invalidation
};

/**
 * Fetches current organization information including settings and configuration.
 * @returns Organization domain model with all settings
 */
export const getOrganization = async (): Promise<Organization> => {
  const organizationData = await withApiErrorHandler(
    '/api/organizations/me',
    ApiErrorCode.ORGANIZATION_LOAD_FAILED,
    () => axiosInstance.get<{ data: any }>('/api/organizations/me')
  );

  return Organization.fromResponse(organizationData);
};

/**
 * Fetches current organization information
 * @returns Organization domain model
 */
export const getOrganizationSummary = async (): Promise<OrganizationSummary> => {
  const organizationData = await withApiErrorHandler(
    '/api/organizations/me/short',
    ApiErrorCode.ORGANIZATION_LOAD_FAILED,
    () => axiosInstance.get<{ data: OrganizationSummaryResponse }>('/api/organizations/me/short')
  );

  return mapOrganizationSummaryFromResponse(organizationData);
};

/**
 * Updates organization settings including name, industry, country, timezone, and work schedule.
 * @param data - Organization update request with new settings
 */
export const updateOrganization = async (data: OrganizationUpdateRequest): Promise<void> => {
  await withApiErrorHandler('/api/organizations/me', ApiErrorCode.ORGANIZATION_UPDATE_FAILED, () =>
    axiosInstance.patch('/api/organizations/me', data)
  );
};

/**
 * Deletes the current organization permanently.
 * This action removes all projects, people, and data associated with the organization.
 */
export const deleteOrganization = async (): Promise<void> => {
  await withApiErrorHandler('/api/organizations/me', ApiErrorCode.ORGANIZATION_DELETE_FAILED, () =>
    axiosInstance.delete('/api/organizations/me')
  );
};

interface PresignedUrlResponse {
  presignedUrl: string;
}

/**
 * Get a signed URL to upload organization logo to S3
 * @param fileName - The name of the logo file
 * @param contentLength - The size of the logo file in bytes
 * @returns A presigned URL for direct S3 upload
 */
export const getLogoUploadUrl = async (
  fileName: string,
  contentLength: number
): Promise<string> => {
  const response = await axiosInstance.get<ApiResponse<PresignedUrlResponse>>(
    '/api/organizations/me/update-logo-url',
    {
      params: {
        fileName,
        contentLength,
      },
    }
  );

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  return response.data.data.presignedUrl;
};

/**
 * Delete the organization's logo
 */
export const deleteOrganizationLogo = async (): Promise<void> => {
  const response = await axiosInstance.delete<ApiResponse<void>>('/api/organizations/me/logo');

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }
};

/**
 * Get the URL to view the organization's logo
 * @returns The URL to the organization's logo
 */
export const getOrganizationLogoUrl = async (): Promise<string> => {
  const response = await axiosInstance.get<ApiResponse<PresignedUrlResponse>>(
    '/api/organizations/me/logo-url'
  );

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  return response.data.data.presignedUrl;
};
